import { api } from '@/lib/api'

import { dateRanges } from '@/shared/datetime/constants/date'

import type {
	AutomationData,
	AutomationSettings,
	Rule,
	RuleDTO,
} from './automation-settings/types'
import type {
	GraylogEventSource,
	GraylogEventSourceInput,
	GraylogEventSourceOutput,
	SynchronousActionDTO,
	SynchronousActionEventDTO,
	SynchronousActionTriggeredAlertDTO,
	TriggerAlertFilters,
} from './types'
import type { ActionData } from '@/features/actions/types'

/**
 * Run a SOAR action synchronously. The endpoint is generic SOAR, but every
 * consumer is Graylog (D19) — the two fetchers below and the automation chain.
 *
 * @param tenant - The tenant ID
 * @param module_data - Action data
 */
export const runSynchronousAction = async <
	TResult extends SynchronousActionDTO = SynchronousActionDTO,
>(
	tenant: string,
	module_data: ActionData,
) => {
	const { data } = await api.post<TResult>(
		`/api/tenants/${tenant}/soar/synchronous_action`,
		module_data,
	)

	return data
}

/**
 * Get Graylog events
 * @param tenant - The tenant ID
 */
export const fetchGraylogEvents = async (tenant: string) =>
	runSynchronousAction<SynchronousActionEventDTO>(tenant, {
		action_name: 'graylog.get_event_definitions',
		action_input: {},
		connector_id: null,
	})

/**
 * Fetch Graylog Triggered Alerts
 * @param tenant - The tenant ID
 * @param filters - The filters to apply
 */
export const fetchGraylogTriggeredAlerts = async (
	tenant: string,
	filters?: TriggerAlertFilters,
) => {
	/**
	 * The action `get_triggered_alerts` accepts the following inputs:
	 * - limit: number
	 * - start_time: string
	 * - end_time: string
	 * @see AIR-automation-runtime\wrapper\airautomation\integrations\graylog\graylog_actions\event_definition_actions.py line 88
	 */

	const inputs: Record<string, string | number> = {
		limit: filters?.limit ?? 100,
		start_time:
			filters?.start_time ?? dateRanges.last7Days().start.toISOString(),
		end_time: filters?.end_time ?? dateRanges.last7Days().end.toISOString(),
	}

	// If start_time is provided, add it to the inputs
	// if not, should not be included in the request
	if (filters?.start_time) {
		inputs.start_time = filters.start_time
	}

	// If end_time is provided, add it to the inputs
	// if not, should not be included in the request
	if (filters?.end_time) {
		inputs.end_time = filters.end_time
	}

	return runSynchronousAction<SynchronousActionTriggeredAlertDTO>(tenant, {
		action_name: 'graylog.get_triggered_alerts',
		action_input: inputs,
		connector_id: null,
	})
}

/**
 * Fetch Graylog Automation Settings Details
 * @param tenant - The tenant ID
 * @param rule_id - The rule ID
 */
export const fetchGraylogAutomationSettingsDetails = async (
	tenant: string,
	rule_id: string | null,
	signal?: AbortSignal,
): Promise<Rule> => {
	if (!tenant) throw new Error('Tenant and rule ID are required')

	const {
		data: { rule },
	} = await api.get<RuleDTO>(
		`/api/tenants/${tenant}/event/automation_rules/${rule_id}`,
		{
			signal,
		},
	)

	return rule
}

/**
 * Fetch Graylog Automation Settings
 * @param tenant - The tenant ID
 */
export const fetchAutomationSettingsList = async (
	tenant: string,
	page = 1,
	limit = 20,
	signal?: AbortSignal,
): Promise<AutomationSettings> => {
	const { data } = await api.get<AutomationSettings>(
		`/api/tenants/${tenant}/event/automation_rules?page=${page}&limit=${limit}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Create a Graylog Automation
 * @param tenant - The tenant ID
 * @param automation_data - The automation data
 */
export const createAutomation = async (
	tenant: string,
	automation_data: AutomationData,
) => {
	const { data } = await api.post<AutomationData>(
		`/api/tenants/${tenant}/event/automation_rules`,
		automation_data,
	)

	return data
}

/**
 * Update a Graylog Automation
 * @param tenant - The tenant ID
 * @param rule_id - The rule ID
 * @param automation_data - The automation data
 */
export const updateAutomation = async (
	tenant: string,
	rule_id: string,
	automation_data: AutomationData,
) => {
	const { data } = await api.patch<AutomationData>(
		`/api/tenants/${tenant}/event/automation_rules/${rule_id}`,
		automation_data,
	)

	return data
}

/**
 * Delete a Graylog Automation
 * @param tenant - The tenant ID
 * @param rule_id - The rule ID
 */
export const deleteAutomation = async (tenant: string, rule_id: string) => {
	const { data } = await api.delete(
		`/api/tenants/${tenant}/event/automation_rules/${rule_id}`,
	)

	return data
}

/**
 * Fetch Graylog Event Sources
 * @param tenant - The tenant ID
 */
export const fetchEventSources = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<Array<GraylogEventSource>>(
		`/api/${tenant}/event/sources`,
		{
			signal,
		},
	)

	return data
}

/**
 * Create a Graylog Event Source
 * @param tenant - The tenant ID
 * @param event_source - The event source
 */
export const createEventSource = async (
	tenant: string,
	event_source: GraylogEventSourceInput,
) => {
	const { data } = await api.post<GraylogEventSourceOutput>(
		`/api/${tenant}/event/sources/new`,
		event_source,
	)

	return data
}
